import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { fetchGql } from '@/lib/graphql_client'
import {
  ActivationStatesForPartiesDocument,
  type ActivationStatesForPartiesQuery,
} from '@/gql/graphql'
import { useContextStore } from '@/lib/session/context-store'

export type PartyActivationState =
  ActivationStatesForPartiesQuery['activationStatesForParties'][number]

export const activationStatesByPartyKey = (
  callerTenantId: string | undefined,
  partyIds: readonly string[],
) => ['activation', 'states-by-party', callerTenantId ?? 'self', ...partyIds] as const

/**
 * Batch-fetch activation state for a set of party ids, indexed by partyId.
 *
 * Merge key: counterpartyPartyId (= legal_entity.party_id). Follows the same
 * discipline as useAgreementStatusForParties — deduped, sorted key for cache
 * stability; empty Map on disabled/error, never throws.
 *
 * Absent party in the result = no activation row OR unreadable tenant.
 * The two are deliberately indistinguishable — render nothing for both.
 */
export function useActivationStatesForParties(
  partyIds: readonly string[],
): UseQueryResult<Map<string, PartyActivationState>> {
  const callerTenantId = useContextStore((s) => s.active?.tenantId)
  // Stable, deduped, sorted key so row order/re-renders don't thrash the cache.
  const ids = [...new Set(partyIds.filter(Boolean))].sort()

  return useQuery({
    queryKey: activationStatesByPartyKey(callerTenantId, ids),
    enabled: Boolean(callerTenantId) && ids.length > 0,
    staleTime: 30_000,
    queryFn: async (): Promise<Map<string, PartyActivationState>> => {
      const data = await fetchGql(ActivationStatesForPartiesDocument, { partyIds: ids })
      return new Map(
        (data.activationStatesForParties ?? []).map((s) => [s.partyId, s]),
      )
    },
  })
}
